import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery, {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import BioentitySearchInput, {BioentitySearchInputProps} from 'inventory/BioentitySearchInput';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  AdapterEquipmentIdsQuery,
  AdapterEquipmentIdsQueryVariables,
} from './__generated/AdapterEquipmentIdsQuery';
import {
  EquipmentSearchInputQuery,
  EquipmentSearchInputQueryVariables,
} from './__generated/EquipmentSearchInputQuery';
import {EQUIPMENT_SCHEMA_INTERFACE_ID} from './EquipmentConstants';

type EquipmentSearchInputProps = Omit<
  BioentitySearchInputProps & {adapterId?: string | null},
  'bioentityTypes' | 'placeholderText' | 'tagSchemaOrInterfaceId'
>;

export const ADAPTER_EQUIPMENT_IDS_QUERY = gql`
  query AdapterEquipmentIdsQuery($adapterId: StrictString!) @skipAudit @engComponent(name: INSTRUMENTS) {
    equipmentIdsForAdapter(adapterId: $adapterId)
  }
`;

export default function EquipmentSearchInput(props: EquipmentSearchInputProps): JSX.Element {
  const equipmentResponse = useSkippableBenchlingQuery<
    AdapterEquipmentIdsQuery,
    AdapterEquipmentIdsQueryVariables
  >(ADAPTER_EQUIPMENT_IDS_QUERY, {
    variables: {adapterId: props.adapterId!},
    skip: !props.adapterId,
  });

  if (props.adapterId) {
    if (!equipmentResponse || equipmentResponse.type !== QueryResultType.SUCCESS) {
      return <CenteredHorizontalThrobber />;
    }
    return (
      <BioentitySearchInput
        bioentityTypes={[ModelTypeConstants.BASIC_FOLDER_ITEM]}
        placeholderText="Search for an instrument"
        tagSchemaOrInterfaceId={EQUIPMENT_SCHEMA_INTERFACE_ID}
        validIds={equipmentResponse.data.equipmentIdsForAdapter}
        {...props}
      />
    );
  }
  return (
    <BioentitySearchInput
      bioentityTypes={[ModelTypeConstants.BASIC_FOLDER_ITEM]}
      placeholderText="Search for an instrument"
      tagSchemaOrInterfaceId={EQUIPMENT_SCHEMA_INTERFACE_ID}
      {...props}
    />
  );
}

export const EQUIPMENT_SEARCH_INPUT_QUERY: DocumentNode = gql`
  query EquipmentSearchInputQuery($id: StrictString!) @skipAudit @engComponent(name: INSTRUMENTS) {
    equipment(id: $id) {
      ... on EquipmentNode {
        ...BioentitySummaryGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('BioentitySummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function QueryingEquipmentSearchInput(
  props: Omit<EquipmentSearchInputProps, 'selectedItems'> & {equipmentId: string | null}
): JSX.Element {
  return (
    <BenchlingQuery<EquipmentSearchInputQuery, EquipmentSearchInputQueryVariables>
      query={EQUIPMENT_SEARCH_INPUT_QUERY}
      variables={{id: props.equipmentId!}}
      renderLoading={() => <CenteredHorizontalThrobber />}
      skip={props.equipmentId == null}
    >
      {(data) => (
        <EquipmentSearchInput
          {...props}
          selectedItems={props.equipmentId ? accessibleObjs([data?.equipment]) : []}
        />
      )}
    </BenchlingQuery>
  );
}
